// src/wallets/AbstractSocialWallet.tsx
import { Web3AuthCore } from "@web3auth/core";

// src/configs/chainConfig.tsx
import { CHAIN_NAMESPACES } from "@web3auth/base";

// src/constants.tsx
var HIDDEN_LOGIN_METHODS = [
  "reddit",
  "apple",
  "line",
  "kakao",
  "linkedin",
  "weibo",
  "wechat",
  "email_passwordless"
];
var INFURA_ID = "f36f7f706a58477884ce6fe89165666c";
var CHAIN_ID_TO_INFURA_NAME = {
  1: "mainnet",
  10: "optimism-mainnet",
  1313161554: "aurora-mainnet",
  1313161555: "aurora-testnet",
  137: "polygon-mainnet",
  420: "optimism-goerli",
  42161: "arbitrum-mainnet",
  421613: "arbitrum-goerli",
  43113: "avalanche-fuji",
  43114: "avalanche-mainnet",
  5: "goerli",
  80001: "polygon-mumbai"
};

// src/configs/chainConfig.tsx
var getChainConfig = (chainId) => ({
  chainNamespace: CHAIN_NAMESPACES.EIP155,
  chainId: `0x${chainId.toString(16)}`,
  rpcTarget: `https://${CHAIN_ID_TO_INFURA_NAME[chainId]}.infura.io/v3/${INFURA_ID}`
});

// src/configs/web3AuthConfig.tsx
var getWeb3AuthConfig = (chainId) => {
  var _a;
  return {
    chainConfig: getChainConfig(chainId),
    clientId: (_a = process.env.REACT_APP_ZEROKIT_WEB3AUTH_CLIENT_ID) != null ? _a : "BEjNZMt6TPboj3TfHM06MP8Yxz7cKQX6eK3KZzVhrIMi7jALcZHxJv5o3fDLM7EL4QfPlf2AV_qe155vyR3QxiU",
    enableLogging: false
  };
};

// src/wallets/AbstractSocialWallet.tsx
import { OpenloginAdapter } from "@web3auth/openlogin-adapter";

// src/configs/openloginAdapterConfig.tsx
var getOpenloginAdapterConfig = () => {
  var _a, _b, _c, _d, _e, _f, _g, _h, _i, _j;
  return {
    adapterSettings: {
      clientId: (_a = process.env.REACT_APP_ZEROKIT_WEB3AUTH_CLIENT_ID) != null ? _a : "BEjNZMt6TPboj3TfHM06MP8Yxz7cKQX6eK3KZzVhrIMi7jALcZHxJv5o3fDLM7EL4QfPlf2AV_qe155vyR3QxiU",
      loginConfig: {
        twitch: {
          clientId: (_b = process.env.REACT_APP_ZEROKIT_TWITCH_CLIENT_ID) != null ? _b : "vidpa4wsenhzfy8h5qak0wznqckmjn",
          name: "Twitch",
          typeOfLogin: "twitch",
          "verifier": "zerokit-twitch"
        },
        google: {
          clientId: (_c = process.env.REACT_APP_ZEROKIT_GOOGLE_CLIENT_ID) != null ? _c : "858644905236-j3v174qpg83pt1nkhb861l9up8762gnh.apps.googleusercontent.com",
          name: "Google",
          typeOfLogin: "google",
          verifier: "zerokit-google"
        },
        facebook: {
          clientId: (_d = process.env.REACT_APP_ZEROKIT_FACEBOOK_CLIENT_ID) != null ? _d : "735339824616125",
          name: "Facebook",
          typeOfLogin: "facebook",
          verifier: "zerokit-facebook"
        },
        twitter: {
          clientId: (_e = process.env.REACT_APP_ZEROKIT_TWITTER_CLIENT_ID) != null ? _e : "bjt1MzABwIvAYl3oWYmffTqw1eKqP3sH",
          name: "Twitter",
          typeOfLogin: "twitter",
          verifier: "zerokit-twitter2",
          jwtParameters: {
            domain: (_f = process.env.REACT_APP_ZEROKIT_TWITTER_DOMAIN) != null ? _f : "https://zerokit.us.auth0.com",
            verifierIdField: "sub"
          }
        },
        github: {
          clientId: (_g = process.env.REACT_APP_ZEROKIT_GITHUB_CLIENT_ID) != null ? _g : "bjt1MzABwIvAYl3oWYmffTqw1eKqP3sH",
          name: "Github",
          typeOfLogin: "github",
          verifier: "zerokit-github",
          jwtParameters: {
            domain: (_h = process.env.REACT_APP_ZEROKIT_GITHUB_DOMAIN) != null ? _h : "https://zerokit.us.auth0.com",
            verifierIdField: "sub"
          }
        },
        discord: {
          clientId: (_i = process.env.REACT_APP_ZEROKIT_DISCORD_CLIENT_ID) != null ? _i : "1072625703675236412",
          name: "Discord",
          typeOfLogin: "discord",
          verifier: "zerokit-discord"
        }
      },
      uxMode: "popup",
      network: (_j = process.env.REACT_APP_ZEROKIT_WEB3AUTH_NETWORK) != null ? _j : "cyan"
    }
  };
};

// src/wallets/AbstractSocialWallet.tsx
var AbstractSocialWallet = class {
  constructor(options = {}) {
    this.options = options;
  }
  async init(chainId, adapterSettings, Web3AuthClass) {
    this.web3AuthConfig = { ...getWeb3AuthConfig(chainId), ...this.options };
    this.web3Auth = new (Web3AuthClass || Web3AuthCore)(this.web3AuthConfig);
    const openLoginAdapter = new OpenloginAdapter(getOpenloginAdapterConfig());
    this.web3Auth.configureAdapter(openLoginAdapter);
    if (adapterSettings) {
      openLoginAdapter.setAdapterSettings(adapterSettings);
    }
  }
  async disconnect() {
    if (this.web3Auth) {
      await this.web3Auth.logout();
    }
  }
};

// src/wallets/AbstractBaseSocialWallet.tsx
import { Web3AuthCore as Web3AuthCore2 } from "@web3auth/core";
import { ADAPTER_STATUS, WALLET_ADAPTERS } from "@web3auth/base";
var AbstractBaseSocialWallet = class extends AbstractSocialWallet {
  async connect(chainId, adapterSettings) {
    await this.init(chainId, adapterSettings);
    if (this.web3Auth && this.web3Auth instanceof Web3AuthCore2) {
      try {
        if (this.web3Auth.status === ADAPTER_STATUS.NOT_READY) {
          await this.web3Auth.init();
        }
        let { provider } = this.web3Auth;
        if (!provider) {
          provider = await this.web3Auth.connectTo(WALLET_ADAPTERS.OPENLOGIN, {
            loginProvider: this.loginProvider
          });
        }
        return provider;
      } catch (error) {
        console.log(error);
      }
    }
  }
};

// src/wallets/GoogleSocialWallet.tsx
var GoogleSocialWallet = class extends AbstractBaseSocialWallet {
  constructor() {
    super(...arguments);
    this.loginProvider = "google";
  }
};

// src/wallets/FacebookSocialWallet.tsx
var FacebookSocialWallet = class extends AbstractBaseSocialWallet {
  constructor() {
    super(...arguments);
    this.loginProvider = "facebook";
  }
};

// src/wallets/GithubSocialWallet.tsx
var GithubSocialWallet = class extends AbstractBaseSocialWallet {
  constructor() {
    super(...arguments);
    this.loginProvider = "github";
  }
};

// src/wallets/DiscordSocialWallet.tsx
var DiscordSocialWallet = class extends AbstractBaseSocialWallet {
  constructor() {
    super(...arguments);
    this.loginProvider = "discord";
  }
};

// src/wallets/TwitchSocialWallet.tsx
var TwitchSocialWallet = class extends AbstractBaseSocialWallet {
  constructor() {
    super(...arguments);
    this.loginProvider = "twitch";
  }
};

// src/wallets/TwitterSocialWallet.tsx
var TwitterSocialWallet = class extends AbstractBaseSocialWallet {
  constructor() {
    super(...arguments);
    this.loginProvider = "twitter";
  }
};

// src/wallets/SocialWallet.tsx
import { Web3Auth } from "@web3auth/modal";
import { ADAPTER_STATUS as ADAPTER_STATUS2, WALLET_ADAPTERS as WALLET_ADAPTERS2 } from "@web3auth/base";
var SocialWallet = class extends AbstractSocialWallet {
  constructor(options = {}) {
    super({
      uiConfig: {
        loginMethodsOrder: ["email_passwordless"]
      },
      authMode: "WALLET",
      ...options
    });
  }
  async connect(chainId, adapterSettings) {
    await this.init(chainId, adapterSettings, Web3Auth);
    if (this.web3Auth && this.web3Auth instanceof Web3Auth) {
      try {
        if (this.web3Auth.status === ADAPTER_STATUS2.NOT_READY) {
          await this.web3Auth.initModal({
            modalConfig: {
              [WALLET_ADAPTERS2.OPENLOGIN]: {
                label: "openlogin",
                loginMethods: {
                  ...HIDDEN_LOGIN_METHODS.reduce((hiddenLoginLoginConfig, hiddenLoginMethod) => ({ ...hiddenLoginLoginConfig, [hiddenLoginMethod]: { typeOfLogin: hiddenLoginMethod, showOnModal: false } }), {})
                }
              }
            }
          });
        }
        let { provider } = this.web3Auth;
        if (!provider) {
          provider = await this.web3Auth.connect();
        }
        return provider;
      } catch (error) {
        console.log(error);
      }
    }
  }
};

// src/index.tsx
import { getChainConfig as getChainConfig2 } from "@web3auth/base";
export {
  AbstractSocialWallet,
  DiscordSocialWallet,
  FacebookSocialWallet,
  GithubSocialWallet,
  GoogleSocialWallet,
  SocialWallet,
  TwitchSocialWallet,
  TwitterSocialWallet,
  getChainConfig2 as getChainConfig,
  getOpenloginAdapterConfig,
  getWeb3AuthConfig
};
